import { useEffect, useMemo } from "react";

import constants from "../../constants";
import { getLuminance } from "../../services/helper";

const GOOGLE_FONTS_MAP = {
  Nunito: "Nunito:wght@400;500;600;700",
  Pacifico: "Pacifico:wght@400",
  Roboto: "Roboto:wght@400;500;700",
  "Roboto Mono": "Roboto+Mono:wght@400;500;700",
  Lobster: "Lobster:wght@400",
  Lora: "Lora:wght@400;500;600;700",
  Merriweather: "Merriweather:wght@400;700",
  Montserrat: "Montserrat:wght@400;500;600;700",
  Caveat: "Caveat:wght@400;500;600;700",
  "EB Garamond": "EB+Garamond:wght@400;500;600;700",
  "Playfair Display": "Playfair+Display:wght@400;500;600;700",
  Oswald: "Oswald:wght@400;500;600;700",
};

export default function ThemeProvider({ children, themeData, isProplanActive }) {
  const fontFamily = themeData?.fontFamily || constants.fontFamily.defaultFont;

  const isPrimaryColorPresent = !!themeData?.primaryColor;
  const isBackgroundColorPresent = !!themeData?.backgroundColor;

  const backgroundColorLuminance = useMemo(() => {
    return isBackgroundColorPresent ? getLuminance(themeData.backgroundColor) : 1;
  }, [isBackgroundColorPresent, themeData?.backgroundColor]);

  // Calculate primary color luminance with fallback logic
  const primaryColorLuminance = useMemo(() => {
    // If primary color exists, use its luminance
    if (isPrimaryColorPresent) {
      return getLuminance(themeData.primaryColor);
    }

    // If only background exists, invert its luminance to get contrasting color
    if (!isPrimaryColorPresent && isBackgroundColorPresent) {
      // For dark backgrounds (low luminance), use white as primary
      // For light backgrounds (high luminance), use black as primary
      return backgroundColorLuminance > 0.5 ? 0 : 1; // 0 for black, 1 for white
    }

    // Default fallback if neither exists
    return 0;
  }, [
    themeData?.primaryColor,
    isPrimaryColorPresent,
    isBackgroundColorPresent,
    backgroundColorLuminance,
  ]);

  useEffect(() => {
    // Handle colors
    if (isProplanActive && themeData) {
      // Calculate colors when primary is not present but background is
      const calculatedPrimaryColor = isPrimaryColorPresent
        ? themeData.primaryColor
        : backgroundColorLuminance > 0.5
          ? "#000000"
          : "#FFFFFF";

      // Calculate secondary color - if primary is not present and background is dark
      let calculatedSecondaryColor = themeData?.secondaryColor;
      if (!isPrimaryColorPresent && isBackgroundColorPresent && backgroundColorLuminance <= 0.5) {
        calculatedSecondaryColor = "#ffffff33"; // white with 20% opacity
      }

      // Set colors
      document.documentElement.style.setProperty("--color-primary", calculatedPrimaryColor);
      document.documentElement.style.setProperty("--color-secondary", calculatedSecondaryColor);
      themeData?.backgroundColor &&
        document.documentElement.style.setProperty("--color-background", themeData.backgroundColor);

      // Calculate and set font colors based on luminance
      const fontPrimary = primaryColorLuminance > 0.5 ? "#000000" : "#FFFFFF";
      const fontBackground = backgroundColorLuminance > 0.5 ? "#000000" : "#FFFFFF";
      const fontSecondary = "#000000"; // Fixed value as per PRD

      document.documentElement.style.setProperty("--font-primary", fontPrimary);
      document.documentElement.style.setProperty("--font-background", fontBackground);
      document.documentElement.style.setProperty("--font-secondary", fontSecondary);
    }

    // Handle font loading and application
    if (fontFamily) {
      loadGoogleFont(fontFamily);
      document.documentElement.style.setProperty("--font-family", fontFamily);
    }
  }, [
    fontFamily,
    // themeData,
    primaryColorLuminance,
    backgroundColorLuminance,
    isPrimaryColorPresent,
    isBackgroundColorPresent,
  ]);

  const loadGoogleFont = (fontName) => {
    const fontQuery = GOOGLE_FONTS_MAP[fontName];

    if (!fontQuery) {
      console.warn(`Font ${fontName} not found in GOOGLE_FONTS_MAP`);
      return;
    }

    // Remove existing font link if any
    const existingLink = document.querySelector("link[data-font-loader]");

    if (existingLink) {
      existingLink.remove();
    }

    // Add new font link
    const link = document.createElement("link");
    link.href = `https://fonts.googleapis.com/css2?family=${fontQuery}&display=swap`;
    link.rel = "stylesheet";
    link.setAttribute("data-font-loader", "true");
    document.head.appendChild(link);
  };

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}
